import { useState, type FormEvent } from 'react';
import { Link, useParams } from 'react-router';
import { usePlan } from '../../api/hooks/usePlans.ts';
import {
  useCreateShareLink,
  useInviteViewer,
  usePlanStats,
  useRevokeInvite,
  useRevokeShareLink,
  useRevokeViewer,
  useShareLinks,
  useViewers,
} from '../../api/hooks/useSharing.ts';
import { buttonClass, inputClass } from '../../components/AuthCard.tsx';

function fmtDate(iso: string | null | undefined) {
  return iso ? new Date(iso).toLocaleString() : 'never';
}

function Viewers({ planId }: { planId: string }) {
  const viewers = useViewers(planId);
  const invite = useInviteViewer(planId);
  const revokeViewer = useRevokeViewer(planId);
  const revokeInvite = useRevokeInvite(planId);
  const [email, setEmail] = useState('');

  function onInvite(e: FormEvent) {
    e.preventDefault();
    invite.mutate(email, { onSuccess: () => setEmail('') });
  }

  return (
    <section className="mb-10">
      <h2 className="mb-3 text-lg font-semibold text-gray-800">Named viewers</h2>
      <p className="mb-4 text-sm text-gray-500">
        Viewers sign in with their own account; you see who read what, and they can later
        give feedback on specific parts of the plan.
      </p>
      <form onSubmit={onInvite} className="mb-4 flex gap-2">
        <input
          className={inputClass}
          type="email"
          placeholder="name@example.com"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
        />
        <button className={`${buttonClass} w-auto whitespace-nowrap`} disabled={invite.isPending}>
          {invite.isPending ? 'Inviting…' : 'Invite viewer'}
        </button>
      </form>
      {invite.isError && <p className="mb-3 text-sm text-red-600">{invite.error.message}</p>}
      {invite.isSuccess && (
        <p className="mb-3 text-sm text-green-700">
          {invite.data.outcome === 'viewer_added'
            ? 'They already had an account — the plan is now shared with them.'
            : 'Invitation sent by email.'}
        </p>
      )}
      <ul className="space-y-2">
        {viewers.data?.viewers.map((v) => (
          <li key={v.id} className="flex items-center justify-between rounded-lg border border-gray-200 p-3 text-sm">
            <span>
              <span className="font-medium text-gray-800">{v.user.display_name}</span>
              <span className="ml-2 text-gray-500">{v.user.email}</span>
            </span>
            <button className="text-red-500 hover:underline" onClick={() => revokeViewer.mutate(v.id)}>
              Revoke
            </button>
          </li>
        ))}
        {viewers.data?.invites.map((inv) => (
          <li key={inv.id} className="flex items-center justify-between rounded-lg border border-dashed border-gray-300 p-3 text-sm">
            <span>
              <span className="text-gray-600">{inv.email}</span>
              <span className="ml-2 rounded-full bg-amber-50 px-2 py-0.5 text-xs text-amber-700">invite pending</span>
            </span>
            <button className="text-red-500 hover:underline" onClick={() => revokeInvite.mutate(inv.id)}>
              Revoke
            </button>
          </li>
        ))}
        {viewers.data && viewers.data.viewers.length === 0 && viewers.data.invites.length === 0 && (
          <li className="text-sm text-gray-400">No viewers yet.</li>
        )}
      </ul>
    </section>
  );
}

function ShareLinks({ planId }: { planId: string }) {
  const links = useShareLinks(planId);
  const create = useCreateShareLink(planId);
  const revoke = useRevokeShareLink(planId);
  const [label, setLabel] = useState('');
  const [freshToken, setFreshToken] = useState<string>();

  function onCreate(e: FormEvent) {
    e.preventDefault();
    create.mutate(label, {
      onSuccess: (res) => {
        setFreshToken(res.token);
        setLabel('');
      },
    });
  }

  const shareUrl = (token: string) => `${window.location.origin}/shared/${token}`;

  return (
    <section className="mb-10">
      <h2 className="mb-3 text-lg font-semibold text-gray-800">Secret share links</h2>
      <p className="mb-4 text-sm text-gray-500">
        Anyone with the link can read the plan without an account. Label each link by who
        it's for; revoke it any time.
      </p>
      <form onSubmit={onCreate} className="mb-4 flex gap-2">
        <input
          className={inputClass}
          placeholder="Label — e.g. Banque Alpine"
          value={label}
          onChange={(e) => setLabel(e.target.value)}
          required
        />
        <button className={`${buttonClass} w-auto whitespace-nowrap`} disabled={create.isPending}>
          {create.isPending ? 'Creating…' : 'Create link'}
        </button>
      </form>
      {freshToken && (
        <div className="mb-4 rounded-lg bg-green-50 p-3 text-sm">
          <p className="mb-1 font-medium text-green-800">
            Link created — copy it now, it is shown only once:
          </p>
          <div className="flex items-center gap-2">
            <code className="flex-1 overflow-x-auto rounded bg-white px-2 py-1 text-xs">{shareUrl(freshToken)}</code>
            <button
              className="text-green-700 hover:underline"
              onClick={() => void navigator.clipboard.writeText(shareUrl(freshToken))}
            >
              Copy
            </button>
          </div>
        </div>
      )}
      <ul className="space-y-2">
        {links.data?.links.map((link) => (
          <li key={link.id} className="flex items-center justify-between rounded-lg border border-gray-200 p-3 text-sm">
            <span>
              <span className="font-medium text-gray-800">{link.label}</span>
              <span className="ml-2 text-gray-400">last opened: {fmtDate(link.last_used_at)}</span>
            </span>
            <button className="text-red-500 hover:underline" onClick={() => revoke.mutate(link.id)}>
              Revoke
            </button>
          </li>
        ))}
        {links.data && links.data.links.length === 0 && (
          <li className="text-sm text-gray-400">No share links yet.</li>
        )}
      </ul>
    </section>
  );
}

function Stats({ planId }: { planId: string }) {
  const stats = usePlanStats(planId);
  return (
    <section>
      <h2 className="mb-3 text-lg font-semibold text-gray-800">Reading stats</h2>
      {stats.data && stats.data.viewers.length === 0 && stats.data.links.length === 0 && (
        <p className="text-sm text-gray-400">No views recorded yet.</p>
      )}
      <ul className="space-y-2">
        {stats.data?.viewers.map((v) => (
          <li key={v.user.id} className="rounded-lg border border-gray-200 p-3 text-sm">
            <div className="flex items-center justify-between">
              <span className="font-medium text-gray-800">{v.user.display_name}</span>
              <span className="text-gray-500">
                {v.view_count} view{v.view_count === 1 ? '' : 's'} · last {fmtDate(v.last_viewed_at)}
              </span>
            </div>
            {v.sections_viewed.length > 0 && (
              <p className="mt-1 text-xs text-gray-500">
                sections read: {v.sections_viewed.join(', ')}
              </p>
            )}
          </li>
        ))}
        {stats.data?.links.map((l) => (
          <li key={l.id} className="rounded-lg border border-gray-200 p-3 text-sm">
            <div className="flex items-center justify-between">
              <span className="font-medium text-gray-800">🔗 {l.label}</span>
              <span className="text-gray-500">
                {l.view_count} view{l.view_count === 1 ? '' : 's'} · last {fmtDate(l.last_viewed_at)}
              </span>
            </div>
          </li>
        ))}
      </ul>
    </section>
  );
}

export default function ShareManager() {
  const { planId } = useParams<{ planId: string }>();
  const plan = usePlan(planId);

  if (plan.isPending) {
    return <main className="p-8 text-center text-sm text-gray-400">Loading…</main>;
  }
  if (plan.isError || !planId) {
    return <main className="p-8 text-center text-sm text-red-600">Could not load this plan.</main>;
  }

  return (
    <main className="mx-auto max-w-3xl p-8">
      <header className="mb-8">
        <Link to={`/plans/${planId}/edit`} className="text-sm text-blue-600 hover:underline">
          ← Back to editor
        </Link>
        <h1 className="text-2xl font-bold tracking-tight">Share “{plan.data.plan.title}”</h1>
      </header>
      <Viewers planId={planId} />
      <ShareLinks planId={planId} />
      <Stats planId={planId} />
    </main>
  );
}
